import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCacheCgdo } from 'app/shared/queries/useCacheCgdo';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconSearch } from '@u21/tabler-icons';

// Actions
import { actionEventsSearch } from 'app/modules/search/sliceSearch';

// Selectors
import {
  selectActionEventsSearchResults,
  selectActionEventsSearchLoading,
} from 'app/modules/search/selectors';
import {
  selectAgentOrgID,
  selectHasReadEventsPermission,
} from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Utils

// Helpers
import { formatActionEventOptions } from 'app/modules/search/helpers';
import { ActionEventSearchOptions, MatchType } from 'app/modules/search/enum';
import { selectSearchV2Enabled } from 'app/shared/featureFlags/selectors';
import { useQueryClient } from '@tanstack/react-query';
import { ActionEventSearchPayload } from 'app/modules/search/payload';
import {
  searchActionEvent,
  useActionEventSearch,
} from 'app/modules/actionEvents/queries/useActionEventSearch';
import { ACTION_EVENT_QUERY_KEYS } from 'app/modules/actionEvents/queries/keys';

export type U21ActionEventSearchProps<TClearable extends boolean> =
  DistributiveOmit<
    U21SelectSearchProps<string, TClearable>,
    'options' | 'onSearch'
  >;

export const U21ActionEventSearch = <TClearable extends boolean>(
  props: U21ActionEventSearchProps<TClearable>,
) => {
  const { multi, value } = props;
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [newPayload, setNewPayload] = useState<ActionEventSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const { data: v1Data } = useSelector(selectActionEventsSearchResults);
  const v1Loading = useSelector(selectActionEventsSearchLoading);
  const hasReadEventsPermissions = useSelector(selectHasReadEventsPermission);

  const isV2SearchUsed = useMemo(
    () => Boolean(isSearchV2Enabled),
    [isSearchV2Enabled],
  );

  const {
    actionEvents,
    query: { isFetching },
    refreshActionEvents,
  } = useActionEventSearch(newPayload, isV2SearchUsed);

  const data = useMemo(
    () => (isV2SearchUsed ? actionEvents : v1Data),
    [actionEvents, isV2SearchUsed, v1Data],
  );

  const loading = useMemo(
    () => (isV2SearchUsed ? isFetching : v1Loading),
    [isFetching, isV2SearchUsed, v1Loading],
  );

  const options = useMemo(() => formatActionEventOptions(data), [data]);

  const orgId = useSelector(selectAgentOrgID);
  const { mutate: cacheCgdo } = useCacheCgdo();

  const cachePrefetch = useCallback(
    (newValues: string[]) => {
      if (!isV2SearchUsed) return;
      const prevSet = new Set(Array.isArray(value) ? value : []);
      newValues.forEach((i) => {
        if (prevSet.has(i)) return;
        cacheCgdo({
          object_type: 'action_event',
          hashed_id: computeDlHashIdForCgdo(orgId, String(i)),
        });
      });
    },
    [cacheCgdo, isV2SearchUsed, orgId, value],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadEventsPermissions) {
      return;
    }

    let missingValues: string[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }
    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;

      if (isV2SearchUsed) {
        const payload: ActionEventSearchPayload = {
          offset: OFFSET_DEFAULT,
          limit: missingValues.length,
          search_conditions: [
            {
              phrases: missingValues.map(String),
              field: ActionEventSearchOptions.EXTERNAL_ID,
              match_type: MatchType.EXACT,
            },
          ],
        };

        queryClient.prefetchQuery({
          queryKey: ACTION_EVENT_QUERY_KEYS.getActionEventSearch(payload),
          queryFn: () => searchActionEvent(payload),
        });

        setNewPayload(payload);
      } else {
        missingValues.map((i) =>
          dispatch(
            actionEventsSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: String(i),
              type: 'external_id',
            }),
          ),
        );
      }
    }
  }, [
    dispatch,
    hasReadEventsPermissions,
    isV2SearchUsed,
    options,
    queryClient,
    value,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        if (isV2SearchUsed) {
          setNewPayload({
            offset: OFFSET_DEFAULT,
            limit: INFINITE_SCROLL_LIMIT,
            search_conditions: [
              {
                phrases: [query],
                field: ActionEventSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          });
        } else {
          dispatch(
            actionEventsSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: query || '',
              type: 'external_id',
            }),
          );
        }
      }
    },

    [dispatch, isV2SearchUsed],
  );

  if (!hasReadEventsPermissions) {
    return null;
  }

  const sharedProps: DistributivePick<
    U21SelectSearchProps<string, TClearable>,
    | 'loading'
    | 'onExactSearch'
    | 'onSearch'
    | 'options'
    | 'startIcon'
    | 'noOptionsText'
  > = {
    loading,
    onExactSearch: isV2SearchUsed
      ? async (searchTerms: string[]) => {
          const payload: ActionEventSearchPayload = {
            offset: OFFSET_DEFAULT,
            limit: searchTerms.length,
            search_conditions: [
              {
                phrases: searchTerms.map(String),
                field: ActionEventSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          };

          await queryClient.prefetchQuery({
            gcTime: Infinity,
            queryKey: ACTION_EVENT_QUERY_KEYS.getActionEventSearch(payload),
            queryFn: () => searchActionEvent(payload),
          });
          refreshActionEvents();
        }
      : undefined,
    onSearch,
    options,
    startIcon: <IconSearch />,
    noOptionsText: searchValue
      ? 'No results found'
      : 'Type to search or paste a list of Action Event IDs',
  };

  if (multi) {
    const { onChange, ...rest } = props;
    return (
      <U21SelectSearch<string, TClearable>
        {...sharedProps}
        {...rest}
        multi
        onChange={(newValues, e) => {
          cachePrefetch(newValues);
          onChange(newValues, e);
        }}
      />
    );
  }
  const { onChange, ...rest } = props;
  return (
    <U21SelectSearch<string, TClearable>
      {...sharedProps}
      {...rest}
      onChange={(newValue, e) => {
        if (newValue) {
          cachePrefetch([newValue]);
        }
        onChange(newValue, e);
      }}
    />
  );
};
